import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import endpoints from "../api/endpoints";
import Spinner from "../Components/Spinner";
import {
  ASSET_CARD_DEVICE_DETAILS_FIELDS,
  DEFAULT_ASSET_CARD_CONFIG,
  normalizeAssetCardConfig,
} from "../utils/assetCardConfig";
import "./AssetDeviceDetailsConfigModal.css";

const AssetDeviceDetailsConfigModal = ({ open, organizationId, onClose, onSaved, onAlert }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_ASSET_CARD_CONFIG);
  const [maxItemsLimit, setMaxItemsLimit] = useState(DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems);
  const [availableFields, setAvailableFields] = useState(DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);

  const loadConfig = useCallback(async () => {
    if (!open || !organizationId) return;
    setLoading(true);
    try {
      const res = await axios.get(endpoints.configuration.assetCardDeviceDetails(), {
        params: { organization_id: organizationId },
      });
      setConfig(normalizeAssetCardConfig(res.data?.data?.assetCard));
      setMaxItemsLimit(
        Math.max(
          1,
          Number(res.data?.data?.deviceDetailsMaxItemsLimit || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems)
        )
      );
      setAvailableFields(
        Array.isArray(res.data?.data?.deviceDetailsAvailableFields) && res.data.data.deviceDetailsAvailableFields.length
          ? res.data.data.deviceDetailsAvailableFields
          : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields
      );
    } catch (error) {
      console.error("Failed to load asset display settings", error);
      onAlert?.("error", "Failed to load asset display settings.");
    } finally {
      setLoading(false);
    }
  }, [open, organizationId, onAlert]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  if (!open) return null;

  const selectedFields = config.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields;
  const maxAllowedItems = maxItemsLimit;
  const availableFieldSet = new Set(availableFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);
  const fieldsToRender = ASSET_CARD_DEVICE_DETAILS_FIELDS.filter((field) => availableFieldSet.has(field.value));

  const updateMaxItems = (value) => {
    const nextValue = Number(value);
    setConfig((prev) => ({
      ...prev,
      deviceDetailsMaxItems: Number.isFinite(nextValue)
        ? Math.min(maxAllowedItems, Math.max(1, Math.trunc(nextValue)))
        : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems,
    }));
  };

  const toggleField = (fieldValue) => {
    setConfig((prev) => {
      const current = new Set(prev.deviceDetailsFields || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields);
      if (current.has(fieldValue)) {
        current.delete(fieldValue);
      } else {
        current.add(fieldValue);
      }
      const nextFields = Array.from(current);
      return {
        ...prev,
        deviceDetailsFields: nextFields.filter((field) => availableFieldSet.has(field)),
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(endpoints.configuration.assetCardDeviceDetails(), {
        organization_id: organizationId,
        assetCard: {
          deviceDetailsMaxItems: config.deviceDetailsMaxItems,
          deviceDetailsFields: selectedFields,
        },
      });
      const nextConfig = normalizeAssetCardConfig(res.data?.data?.assetCard);
      setConfig(nextConfig);
      setMaxItemsLimit(
        Math.max(
          1,
          Number(res.data?.data?.deviceDetailsMaxItemsLimit || maxItemsLimit)
        )
      );
      setAvailableFields(
        Array.isArray(res.data?.data?.deviceDetailsAvailableFields) && res.data.data.deviceDetailsAvailableFields.length
          ? res.data.data.deviceDetailsAvailableFields
          : DEFAULT_ASSET_CARD_CONFIG.deviceDetailsFields
      );
      onSaved?.(nextConfig);
      onAlert?.("success", "Device details asset display updated.");
      onClose?.();
    } catch (error) {
      console.error("Failed to save asset display settings", error);
      onAlert?.("error", "Failed to save asset display settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asset-summary-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="asset-summary-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Device details asset display"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="asset-summary-modal-header">
          <div>
            <h2>Device details asset display</h2>
            <p>Choose what asset information appears in the Device Details header.</p>
          </div>
          <button type="button" className="asset-summary-modal-close" onClick={onClose} aria-label="Close">
            x
          </button>
        </header>

        {loading ? (
          <div className="asset-summary-modal-loading">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="asset-summary-control">
              <div>
                <strong>Asset items</strong>
                <small>Choose how many related asset cards to show. Super maximum: {maxAllowedItems}.</small>
              </div>
              <div className="asset-summary-stepper">
                <button type="button" onClick={() => updateMaxItems((config.deviceDetailsMaxItems || 4) - 1)}>
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  max={maxAllowedItems}
                  step="1"
                  value={config.deviceDetailsMaxItems || DEFAULT_ASSET_CARD_CONFIG.deviceDetailsMaxItems}
                  onChange={(event) => updateMaxItems(event.target.value)}
                />
                <button type="button" onClick={() => updateMaxItems((config.deviceDetailsMaxItems || 4) + 1)}>
                  +
                </button>
              </div>
            </div>

            <div className="asset-summary-fields">
              {fieldsToRender.map((field) => {
                const checked = selectedFields.includes(field.value);
                return (
                  <label className={`asset-summary-field ${checked ? "selected" : ""}`} key={field.value}>
                    <input type="checkbox" checked={checked} onChange={() => toggleField(field.value)} />
                    <span>{field.label}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}

        <footer className="asset-summary-modal-footer">
          <button type="button" className="asset-summary-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="asset-summary-primary" onClick={save} disabled={loading || saving}>
            {saving ? "Saving..." : "Save"}
          </button>
        </footer>
      </section>
    </div>
  );
};

export default AssetDeviceDetailsConfigModal;
